import { TableFooter } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import classNames from "classnames";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import type { BeneficiaryLine } from "components/URSSAFModal";
import { distributorTypes } from "components/URSSAFModal";
import Papa from "papaparse";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import variables from "../../../../assets/styles/_variables.module.scss";
import styles from "../ImportFile/style.module.scss";

interface ValidateFileProps {
    onSubmit: (data: BeneficiaryLine[], normFields: any) => void;
    uploadedFile?: File;
    normFields?: any;
    distributorType: number;
}

const lines: BeneficiaryLine[] = [];

export default function ValidateFile(props: ValidateFileProps) {
    const { uploadedFile } = props;
    const [t] = useTranslation();

    const [activeStep, setActiveStep] = useState(0);
    const [parsedRows, setParsedRows] = useState<any[]>([]);
    const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);

    const [normFields, setNormFields] = useState(
        props.normFields ?? {
            socialProfileType: {
                label: "Type de cotisant",
                isOptional: false,
                relatedKey: "",
            },
            socialSecurityNumber: {
                label: "Numéro de sécurité sociale",
                isOptional: true,
                relatedKey: "",
            },
            lastname: {
                label: "Nom ou Dénomination",
                isOptional: false,
                relatedKey: "",
            },
            firstname: { label: "Prénom", isOptional: true, relatedKey: "" },
            // "streetNumber": { label: "Numéro de voie", isOptional: true, relatedKey: '' },
            // "streetNumberMultiplierAdverb": { label: "Indice de voie", isOptional: true, relatedKey: '' },
            street: { label: "Nom de voie", isOptional: true, relatedKey: "" },
            zipCode: { label: "Code postal", isOptional: true, relatedKey: "" },
            city: { label: "Ville", isOptional: true, relatedKey: "" },
            country: { label: "Pays", isOptional: true, relatedKey: "" },
            birthDate: { label: "Date de naissance", isOptional: true, relatedKey: "" },
            incomeNature: {
                label: "Nature du revenu artistique",
                isOptional: true,
                relatedKey: "",
            },
            artworkNature: {
                label: "Nature de l'oeuvre",
                isOptional: true,
                relatedKey: "",
            },
            paymentDate: {
                label: "Date de versement",
                isOptional: false,
                relatedKey: "",
            },
            siretNumber: {
                label: "Numéro de SIRET ou RNA",
                isOptional: true,
                relatedKey: "",
            },
            grossDuty: {
                label: "Rémunération brute ou montant versé",
                isOptional: false,
                relatedKey: "",
            },
            ...(distributorTypes[props.distributorType] === "DCA"
                ? {
                      dcaRevenue: {
                          label: "DCA - Chiffres d'affaires TTC",
                          isOptional: false,
                          relatedKey: "",
                      },
                      dcaCommission: {
                          label: "DCA/OVV - Commissions TTC",
                          isOptional: false,
                          relatedKey: "",
                      },
                  }
                : {}),
        },
    );

    const normFieldsArray = Object.entries(normFields);
    const normFieldsLeft = normFieldsArray.filter(([k, v]: any) => v.relatedKey === "");
    const mandatoryFields = normFieldsArray.filter(([k, v]: any) => v.relatedKey === "" && v.isOptional === false);

    useEffect(() => {
        if (uploadedFile && activeStep == 0) {
            parseFileData(uploadedFile);
            setActiveStep(1);
        }
    }, [uploadedFile, activeStep]);

    const handleNextStep = () => {
        // Regex that catch any alphanumeric characters including characters with an accent like in the french langage and common special characters
        const alphanumericalAndCommonSpecialChars = /^[a-zA-ZÀ-ÿ0-9éèàùâêîôûäëïöüç!@#$%^&*()_+{}[\]:;<>, .?/~\\-]+$/;

        if (activeStep === 1) {
            const rows = [
                ...parsedRows.filter((row) =>
                    Object.values(row).some(
                        (val: any) =>
                            val?.toString().trim().length &&
                            val?.toString().trim().length > 0 &&
                            alphanumericalAndCommonSpecialChars.test(val?.toString()),
                    ),
                ),
            ];

            const mandatory = Object.entries(normFields)
                .filter(([k, v]: any) => v.isOptional === false && v.relatedKey === "")
                .map(([k, v]: any) => v.label)
                .join(", ");

            if (mandatory.length > 0) {
                return toast.error(
                    `${t("Declare.ValidateStep.MandatoryColumns", {
                        mandatory: mandatory,
                    })})})}`,
                );
            }
            rows.forEach((row) => {
                for (const cell in row) {
                    const relatedKey = Object.entries(normFields).find(
                        ([key, options]: any) => options.relatedKey == cell,
                    );

                    if (relatedKey) {
                        // console.log('Done mapping: ', cell);
                        row[relatedKey[0]] = row[cell];
                        if (relatedKey[0] !== cell) delete row[cell];
                    } else {
                        // console.log('Ignored mapping: ', cell);
                        delete row[cell];
                    }
                }
            });
            setParsedRows(rows);
            setActiveStep(activeStep + 1);
            props.onSubmit(rows, normFields);
        }
    };

    const parseFileData = (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            encoding: "UTF-8",
            worker: true,
            complete: function (results) {
                const _normFields = { ...normFields };

                results.data.forEach((row: any) => {
                    for (const cell in row) {
                        // If the file header does not equal one of our internal variable,
                        // just look for the label :)
                        if (!Object.keys(normFields).includes(cell)) {
                            // If we find a label-cell matching, we map!
                            if (
                                Object.values(normFields).some(
                                    (key: any) => key.label.toLowerCase() == cell.toLowerCase(),
                                )
                            ) {
                                const infos =
                                    Object.entries(normFields).find(
                                        ([, options]: any) => options.label.toLowerCase() == cell.toLowerCase(),
                                    ) || [];

                                (_normFields as any)[(infos as any)[0]].relatedKey =
                                    (_normFields as any)[(infos as any)[0]].relatedKey == ""
                                        ? cell
                                        : (_normFields as any)[(infos as any)[0]].relatedKey;
                            }
                        }
                    }
                });

                setNormFields(_normFields);
                setParsedRows(results.data);
                setParsedHeaders(Object.keys(results.data[0] as any));
            },
        });
    };

    const setMappedKey = (e: any, key: string) => {
        setNormFields((prevNormFields: any) => ({
            ...prevNormFields,
            [key]: {
                ...(prevNormFields as any)[key],
                relatedKey: e.target.value,
            },
        }));
    };

    const tableCellColumnNameStyle: CSSProperties = {
        backgroundColor: "#f6f6f6",
        fontSize: "14px",
        borderRight: "var(--border)",
        borderBottomColor: "var(--border-color)",
        fontWeight: "600",
        zIndex: 3,
    };

    const tableCellColumnValueStyle: CSSProperties = {
        borderBottomColor: "var(--border-color)",
    };

    const tableFooterStyle: CSSProperties = {
        position: "sticky",
        bottom: "0",
        backgroundColor: "#f6f6f6",
        outline: "var(--border)",
        zIndex: 3,
    };

    return (
        <div className={classNames(styles.Importer, styles.Validate)}>
            <div className="step-header">
                <h1>{t("Declare.ValidateStep.Title")}</h1>
                <p
                    style={{
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: variables["foreground"],
                    }}
                >
                    {t("Declare.ValidateStep.Subtitle")}
                </p>
            </div>
            {activeStep === 1 && (
                <TableContainer
                    component={Paper}
                    sx={{
                        margin: "24px 0",
                        boxShadow: "none",
                        border: "var(--border)",
                        maxHeight: "418px",
                    }}
                >
                    <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tableCellColumnNameStyle} variant="head"></TableCell>
                                <TableCell sx={{ ...tableCellColumnNameStyle, borderRight: "none" }} variant="head">
                                    {t("Declare.ValidateStep.CorrespondingColumns")}
                                </TableCell>
                                <TableCell sx={{ ...tableCellColumnNameStyle, borderRight: "none" }} variant="head">
                                    {t("Declare.ValidateStep.FileSample")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(normFields).map(([field, options]: any, idx) => (
                                <TableRow key={`line${idx}`}>
                                    <TableCell sx={tableCellColumnNameStyle} component="td" scope="col">
                                        {options.label}
                                    </TableCell>
                                    <TableCell sx={tableCellColumnValueStyle} component="td" scope="col" variant="head">
                                        <StyledSelect
                                            fullWidth
                                            size="small"
                                            sx={{ maxWidth: "320px" }}
                                            onChange={(e) => setMappedKey(e, field)}
                                            required={!options.isOptional}
                                            value={(normFields as any)[field].relatedKey}
                                            error={!normFields[field].relatedKey}
                                        >
                                            {options.isOptional && (
                                                <StyledMenuItem value={"NotMandatory"}>
                                                    <i>Ignorer</i>
                                                </StyledMenuItem>
                                            )}
                                            {parsedHeaders.map((header, jdx) => (
                                                <StyledMenuItem value={header} key={`opt${jdx}`}>
                                                    {header}
                                                </StyledMenuItem>
                                            ))}
                                        </StyledSelect>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableCellColumnValueStyle,
                                            color: variables["foreground"],
                                            fontWeight: "400",
                                        }}
                                        component="td"
                                        scope="col"
                                        variant="head"
                                    >
                                        {parsedRows.slice(0, 1).map((row, kdx) => (
                                            <span key={`${options.relatedKey}-${options.label}-${idx}-${kdx}` || kdx}>
                                                {(row as any)[options.relatedKey]}
                                                {(row as any)[options.relatedKey] && kdx < 2 ? " - ..." : "..."}
                                            </span>
                                        ))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter sx={tableFooterStyle}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                                <TableCell
                                    sx={{
                                        borderBottom: "none",
                                        fontWeight: 600,
                                        color: "var(--success)",
                                    }}
                                >
                                    {normFieldsArray.length - normFieldsLeft.length}/{normFieldsArray.length}{" "}
                                    {t("Declare.ValidateStep.CompletedInfo")}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none" }}></TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}

            {activeStep === 2 && (
                <div style={{ textAlign: "center" }}>
                    <Spinner />
                    <h3>{t("Declare.ValidateStep.LoadingFile")}</h3>
                </div>
            )}

            {activeStep < 2 && (
                <StyledButton
                    buttonType="primary"
                    fullWidth
                    onClick={handleNextStep}
                    className={styles.submitBtn}
                    disabled={mandatoryFields.length > 0}
                >
                    {t("Declare.Next")}
                </StyledButton>
            )}
        </div>
    );
}
